import { useCallback, useMemo, useState } from "react";
import {
  CreditCard,
  LogOut,
  PlugZap,
  UserRound,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import IntegrationsPanel from "../components/integrations/IntegrationsPanel";
import { AccountSettingsPanel } from "../components/settings/AccountSettingsPanel";
import { MembershipSettingsPanel } from "../components/settings/MembershipSettingsPanel";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";
import { Language } from "../utils/dictionary";

type SettingsTabId = "account" | "integrations" | "membership" | "logout";

const copy = {
  account: {
    [Language.SPANISH]: "Cuenta",
    [Language.ENGLISH]: "Account",
    [Language.PORTUGUESE]: "Conta",
  },
  integrations: {
    [Language.SPANISH]: "Integraciones",
    [Language.ENGLISH]: "Integrations",
    [Language.PORTUGUESE]: "Integrações",
  },
  membership: {
    [Language.SPANISH]: "Membresía",
    [Language.ENGLISH]: "Membership",
    [Language.PORTUGUESE]: "Assinatura",
  },
  logout: {
    [Language.SPANISH]: "Cerrar sesión",
    [Language.ENGLISH]: "Log out",
    [Language.PORTUGUESE]: "Sair",
  },
  logoutTitle: {
    [Language.SPANISH]: "Cerrar sesión",
    [Language.ENGLISH]: "Log out",
    [Language.PORTUGUESE]: "Sair",
  },
  logoutBody: {
    [Language.SPANISH]: "Al salir volverás a la pantalla de inicio. Tus integraciones, membresía y datos de cuenta no se modifican.",
    [Language.ENGLISH]: "You will return to the start screen. Your integrations, membership, and account data will not be changed.",
    [Language.PORTUGUESE]: "Você voltará para a tela inicial. Suas integrações, assinatura e dados da conta não serão alterados.",
  },
  logoutAction: {
    [Language.SPANISH]: "Salir de Fluxi",
    [Language.ENGLISH]: "Log out of Fluxi",
    [Language.PORTUGUESE]: "Sair do Fluxi",
  },
};

const LogoutPanel = ({ onLogout }: { onLogout: () => void }) => {
  const { language } = useAuth();

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
          {copy.logoutTitle[language]}
        </h1>
      </header>

      <div className="border-y border-gray-100 py-5">
        <div className="flex max-w-xl items-center justify-between gap-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-gray-950 ring-1 ring-gray-200">
              <LogOut className="h-6 w-6" />
            </div>
            <p className="text-sm leading-6 text-gray-500">
              {copy.logoutBody[language]}
            </p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="inline-flex h-10 shrink-0 items-center justify-center rounded-xl bg-gray-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
          >
            {copy.logoutAction[language]}
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const { language, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTabId>("account");

  const handleLogout = useCallback(() => {
    logout();
    navigate("/");
  }, [logout, navigate]);

  const navItems = useMemo(
    () => [
      {
        id: "account" as const,
        icon: <UserRound className="h-4 w-4" />,
        label: copy.account[language],
      },
      {
        id: "integrations" as const,
        icon: <PlugZap className="h-4 w-4" />,
        label: copy.integrations[language],
      },
      {
        id: "membership" as const,
        icon: <CreditCard className="h-4 w-4" />,
        label: copy.membership[language],
      },
      {
        id: "logout" as const,
        icon: <LogOut className="h-4 w-4" />,
        label: copy.logout[language],
      },
    ],
    [language]
  );

  const renderActiveContent = () => {
    if (activeTab === "account") return <AccountSettingsPanel />;
    if (activeTab === "integrations") return <IntegrationsPanel variant="settings" />;
    if (activeTab === "membership") return <MembershipSettingsPanel />;
    return <LogoutPanel onLogout={handleLogout} />;
  };

  return (
    <div className="box-border flex h-[calc(100vh-8px)] w-full justify-center overflow-hidden bg-[#fbfcff] px-4 py-4 md:w-[calc(100vw-240px)] md:max-w-[calc(100vw-240px)] md:px-8 md:pb-8 md:pt-[clamp(5rem,calc(2rem+12dvh),12rem)]">
      <div className="flex w-full max-w-[900px] min-w-0 items-start">
        <aside className="hidden w-[210px] shrink-0 md:block">
          <nav className="sticky top-0 space-y-1 pr-5">
            {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveTab(item.id)}
                  className={cn(
                    "flex h-11 w-full items-center gap-3 rounded-xl px-3 text-left text-sm font-medium transition-colors",
                    isActive
                      ? "bg-white text-gray-950 ring-1 ring-gray-200"
                      : "text-gray-500 hover:bg-white/70 hover:text-gray-950"
                  )}
                >
                  <span className={cn(isActive ? "text-gray-950" : "text-gray-400")}>
                    {item.icon}
                  </span>
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex min-w-0 flex-1 flex-col border-l border-gray-200/70 md:max-h-[calc(100vh_-_clamp(5rem,calc(2rem+12dvh),12rem)_-_32px)]">
          <div className="border-b border-gray-200/70 bg-[#fbfcff] px-1 pb-3 md:hidden">
            <div className="flex gap-2 overflow-x-auto">
              {navItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveTab(item.id)}
                    className={cn(
                      "inline-flex h-10 shrink-0 items-center gap-2 rounded-xl px-3 text-sm font-semibold",
                      isActive
                        ? "bg-gray-950 text-white"
                        : "bg-white text-gray-600 ring-1 ring-gray-200"
                    )}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="min-h-0 overflow-y-auto px-1 py-4 md:px-8 md:py-0">
            {renderActiveContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
